import { effect, inject, Injectable } from '@angular/core';

import { ConsentService } from 'app/services/consent';
import { environment } from 'environments/environment';

const FACEBOOK_PIXEL_ID = '2198424573757292';
const FACEBOOK_PIXEL_SRC = 'https://connect.facebook.net/en_US/fbevents.js';
const LEADFEEDER_SRC = 'https://lftracker.leadfeeder.com/lftracker_v1_3P1w24dZZVB8mY5n.js';

type FacebookPixel = ((...args: unknown[]) => void) & {
    callMethod?: (...args: unknown[]) => void;
    queue: unknown[][];
    push: unknown;
    loaded: boolean;
    version: string;
};

// The third-party snippets attach themselves to the global window object with no bundled types
type MarketingWindow = Window & {
    fbq?: FacebookPixel;
    _fbq?: FacebookPixel;
    ldfdr?: Record<string, unknown>;
};

@Injectable()
export class MarketingScriptsService {
    private consentService = inject(ConsentService);

    private static loaded = false;

    constructor() {
        effect(() => {
            if (environment.marketingScriptsEnabled && this.consentService.marketing()) {
                this.load();
            }
        });
    }

    private load(): void {
        if (MarketingScriptsService.loaded) {
            return;
        }
        MarketingScriptsService.loaded = true;
        this.consentService.registerLoadedScript('marketing');

        this.loadFacebookPixel();
        this.loadLeadfeeder();
    }

    private loadFacebookPixel(): void {
        const w = window as MarketingWindow;

        if (w.fbq) {
            return;
        }

        const fbq = function (...args: unknown[]) {
            if (fbq.callMethod) {
                fbq.callMethod(...args);
            } else {
                fbq.queue.push(args);
            }
        } as FacebookPixel;

        w.fbq = fbq;
        w._fbq ??= fbq;
        fbq.push = fbq;
        fbq.loaded = true;
        fbq.version = '2.0';
        fbq.queue = [];

        this.appendScript(FACEBOOK_PIXEL_SRC);

        fbq('init', FACEBOOK_PIXEL_ID);
        fbq('track', 'PageView');
    }

    private loadLeadfeeder(): void {
        const w = window as MarketingWindow;
        w.ldfdr = w.ldfdr || {};

        this.appendScript(LEADFEEDER_SRC);
    }

    private appendScript(src: string): void {
        const script = document.createElement('script');
        script.async = true;
        script.src = src;
        document.head.appendChild(script);
    }
}
